import { useMemo, useState, useContext } from 'react';
import dayjs from 'dayjs';
import cs from 'classnames';
import { isNotEmpty, isNotEmptyAny } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { globalExpansionTaskAlias, globalFranchiseeAlias } from '@/common/utils/ways';
import { useGlobalAliases } from '@/common/hook/useGlobalAlias';
import { useTenantCheckInfo } from '@/common/hook/useTenantCheckInfo';
import { BusinessMode, franchiseeShortTitleForMode, TaskStatus } from '@/common/components/business/ExpandStore/ts-config';
import { clusterAndDirectTaskAssign } from '@/common/api/expandStore/clusterAndDirect';
import { TaskMap } from './ts-config';
import { postTaskUpdate } from '@/common/api/expandStore/clusterAndDirect';
import { FollowingStatus } from '@/views/expandstore/pages/expansioncirclesenmatask/ts-config';
import { Col, Form, Row } from 'antd';
import UserInfoContext from '@/layout/context';
import V2Title from '@/common/components/Feedback/V2Title';
import styles from './index.module.less';
import AreaDetailDrawer from '@/common/components/business/AreaDetailDrawer';
import CircleTaskDetailMap from '../CircleTaskDetailMap';
import V2DetailGroup from '@/common/components/Feedback/V2DetailItem/Group';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import V2Form from '@/common/components/Form/V2Form';
import FormBusinessMode from '../../../../components/FormBusinessMode';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import FormFollowingStatus from '../../../../components/FormFollowingStatus';
import FormFranchisee from 'src/common/components/business/ExpandStore/TaskCreateDrawer/components/FormFranchisee';
import FormEmergencyDegree from '../../../../components/FormEmergencyDegree';
import FormManager from '../../../../components/FormManager';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';
import V2FormRangePicker from '@/common/components/Form/V2FormRangePicker/V2FormRangePicker';
import V2FormInputNumber from '@/common/components/Form/V2FormInputNumber/V2FormInputNumber';
import V2FormRangeInput from '@/common/components/Form/V2FormRangeInput/V2FormRangeInput';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import { postDirectTaskUpdate } from '@/common/api/expandStore/expansiontask';
import { buildLocxxDirectTaskUpdateParams } from '@/views/expandstore/pages/expansionlocxxtask/adapters';
import { buildLocxxTaskDetailItems, getLocxxTaskEditableKeys, getPromotionDateValueProps } from './locxxDetail';

interface CircleBasicInfoProps{
  taskDetail:any;
  chancePointList: any;
  title?: string; // 自定义标题
  className?: string;
  refresh?: any;
  isApproval?: boolean; // 是否是审核状态
  hideClusterDetail?: boolean;
  hideOperate?: boolean; // 是否禁止编辑（历史任务等场景）
  isLocxx?: boolean;
}
interface DrawerData{
  open:boolean;
  id:string
}

/**
 * 拓店任务-详情-基本信息
 * @description 加盟+直营A（基诺浦）、加盟+直营B（一鸣）
 */
const CircleBasicInfo = (props:CircleBasicInfoProps) => {

  const {
    // /** 当前租户的拓店任务模板是否“加盟+直营A（基诺浦）”，未确定时为null */
    // isStandardAndClusterATaskTemplate,
    /** 当前租户的拓店任务模板是否“加盟+直营B（一鸣）”，未确定时为null */
    isStandardAndClusterBTaskTemplate,
  } = useTenantCheckInfo();

  const [form] = Form.useForm();

  /** 获取开店区域别名 */
  const {
    openingArea: openingAreaAlias, // 商圈别名
  } = useGlobalAliases();

  const { taskDetail, chancePointList, title, className, refresh, isApproval, hideClusterDetail, hideOperate, isLocxx = false } = props;
  const [drawerData, setDrawerData] = useState<DrawerData>({ // 选址地图详情抽屉
    open: false,
    id: ''
  });
  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息
  const franchiseeAlias = globalFranchiseeAlias(globalAliasItemVOs);
  const expansionTaskAlias = globalExpansionTaskAlias(globalAliasItemVOs);

  const basicTaskInfo = useMemo(() => {
    if (!isNotEmptyAny(taskDetail)) return {};
    const { modelClusterInfo, manager, emergencyDegree, emergencyDegreeName, targetProvinceName, targetCityName, targetDistrictName, signShopCount, expectDropInDate } = taskDetail;

    const updatedStandardFranchiseeId = taskDetail.standardFranchiseeId;
    const updatedStandardFranchiseeName = taskDetail.standardFranchiseeName;
    const updatedStandardFranchiseeUniqueId = taskDetail.standardFranchiseeUniqueId;

    form.setFieldsValue({
      businessMode: taskDetail.businessMode, // 业务模式
      standardFranchiseeId: updatedStandardFranchiseeId, // 加盟商id
      followingStatus: taskDetail.followingStatus, // 跟进状态
    });


    const locxxTargetAddress = taskDetail.targetArea || `${targetProvinceName && targetProvinceName !== targetCityName ? targetProvinceName + '/' : ''}${targetCityName ? targetCityName + '/' : ''}${targetDistrictName ?? ''}`;
    return {
      id: taskDetail.id,
      modelClusterName: modelClusterInfo?.modelClusterName,
      manager,
      emergencyDegree,
      emergencyDegreeName,
      targetAdress: locxxTargetAddress,
      signShopCount,
      expectDropInDate,
      standardFranchiseeId: updatedStandardFranchiseeId,
      standardFranchiseeName: (updatedStandardFranchiseeName || '-') + (updatedStandardFranchiseeUniqueId ? '/' + updatedStandardFranchiseeUniqueId : ''),
      followingStatus: taskDetail['followingStatus'],
      followingStatusName: taskDetail['followingStatusName'],
      businessModeName: taskDetail['businessModeName'],
      businessMode: taskDetail['businessMode'],
      standardFranchiseeUniqueName: taskDetail['standardFranchiseeUniqueName'],
    };
  }, [taskDetail]);

  /** 跟进状态是否禁用：
   * 加盟+直营A（基诺浦）-已确定、已开业
   * 加盟+直营B（一鸣）-已完成、已关闭
   * */
  const followingStatusDisabled = isStandardAndClusterBTaskTemplate
    ? [TaskStatus.Complete, TaskStatus.Closed].includes(+taskDetail.status)
    : [FollowingStatus.Confirmed, FollowingStatus.Opened].includes(+basicTaskInfo.followingStatus);
  const locxxDetailItemMap = buildLocxxTaskDetailItems(taskDetail || {}, basicTaskInfo || {}).reduce<Record<string, { label: string; value: string }>>((prev, item) => {
    prev[item.key] = item;
    return prev;
  }, {});

  const methods = useMethods({

    handleClickClusterName: () => {
      const modelClusterId = taskDetail?.modelClusterInfo?.modelClusterId;
      if (!modelClusterId) return;
      setDrawerData({
        open: true,
        id: modelClusterId
      });
    },

    /**
     * @description 加盟商字段标题
     * @param businessMode 【加盟+直营】拓店任务模板场景下【业务模式】字段值
     * @return 判断是否联营模式，需要特殊显示
     */
    franchiseeTitleForMode: (businessMode) => {
      const isJoint = businessMode === BusinessMode.Joint;
      return isJoint ? '联营商姓名' : `${franchiseeAlias}姓名`;
    },

    /**
     * 设置detail item 值
     * @param key
     * @returns
     */
    getDetailItemValue: (key) => {
      if (key === 'modelClusterName' && !hideClusterDetail) { // 所属商圈
        return <a className={'color-primary-dark'} style={{ color: '#2441B3' }} onClick={methods.handleClickClusterName}>{basicTaskInfo[key] || '-'}</a>;
      } else if (key === 'emergencyDegreeName' && (basicTaskInfo[key] === '紧急' || basicTaskInfo[key] === '非常紧急')) { // 紧急度
        return <span style={{ color: 'red' }}>{(basicTaskInfo[key] || '-')}</span>;
      } else {
        return basicTaskInfo[key] || '-';
      }
    },

    /**
     * 设置detail item 是否能编辑
     * @param key
     * @returns
     */
    getDetailItemAllowEdit: (key) => {
      if (isLocxx) {
        const hasPermission = taskDetail.permissions?.some(item => item.event === 'standardTask:edit');
        return !isApproval && !hideOperate && hasPermission && getLocxxTaskEditableKeys().includes(key);
      }
      const hasPermission = taskDetail.permissions?.find(item => item.event === 'standardTask:edit');
      if (!hasPermission) return false; // 如果是为空，则不能编辑
      if (isApproval || hideOperate) return false; // 如果是审核状态或禁用（历史任务等场景），则不能编辑

      if (key === 'manager') {
        // 待指派时才可选择开发经理
        return taskDetail?.status === TaskStatus.WaitAssign;
      } else if (key === 'businessModeName') {
        return true;
      } else if (key === 'standardFranchiseeName') {
        return true;
      } else if (key === 'emergencyDegreeName') {
        return true;
      } else if (key === 'expectDropInDate') {
        return true;
      } else if (key === 'followingStatusName' && !followingStatusDisabled) { // 跟进状态
        return true;
      }
      return false;
    },

    /**
     * 信息编辑
     */
    updateTaskInfo: async(field) => {
      form.validateFields().then(async (values) => {
        const curFieldValue = values[field];
        const isFieldBusinessMode = field === 'businessMode';
        // 传可编辑的所有字段的值，如果不传，接口无法区分传了null和不传，会把全部没传的可编辑字段值都置为null
        const params: any = {
          businessMode: basicTaskInfo.businessMode,
          followingStatus: basicTaskInfo.followingStatus,
          emergencyDegree: basicTaskInfo.emergencyDegree,
          expectDropInDate: basicTaskInfo.expectDropInDate,
          standardFranchiseeId: isFieldBusinessMode ? null : basicTaskInfo.standardFranchiseeId, // 模式变更时，需要清空之前选择加盟商
          [field]: curFieldValue,
        };
        await postTaskUpdate({ id: basicTaskInfo['id'], ...params });
        V2Message.success('编辑成功');
        refresh?.();
      // }).catch((err) => {
      //   err && showInvalidFieldMsg(err.errorFields, 1);
      });
    },

    /**
     * 设置detail item 编辑内容
     * @param key
     * @returns
     */
    getDetailItemEditConfig: (key) => {
      if (isApproval) return undefined; // 如果是审核状态，则不能编辑
      if (isLocxx) {
        if (key === 'marketingPurposeName') {
          return {
            formCom: <V2FormSelect label='营销目的' name='marketingPurpose' options={[{ label: '品牌宣传', value: 6 }, { label: '获客', value: 7 }, { label: '售卖', value: 8 }]} />,
            onClick() { form.setFieldValue('marketingPurpose', taskDetail.marketingPurpose); },
            onCancel() { form.setFieldValue('marketingPurpose', taskDetail.marketingPurpose); },
            onOK: async() => {
              const values = await form.validateFields(['marketingPurpose']);
              await postDirectTaskUpdate(buildLocxxDirectTaskUpdateParams({ id: taskDetail.id, ...values }));
              V2Message.success('编辑成功');
              refresh?.();
            },
          };
        }
        if (key === 'areaRequirement') {
          return {
            formCom: <V2FormRangeInput label='面积范围' name={['areaRequirementMin', 'areaRequirementMax']} min={0} precision={1} minPlaceholder='最小面积' maxPlaceholder='最大面积' extra='㎡' />,
            onClick() {
              form.setFieldsValue({ areaRequirementMin: taskDetail.areaRequirementMin, areaRequirementMax: taskDetail.areaRequirementMax });
            },
            onCancel() {
              form.setFieldsValue({ areaRequirementMin: taskDetail.areaRequirementMin, areaRequirementMax: taskDetail.areaRequirementMax });
            },
            onOK: async() => {
              const values = await form.validateFields(['areaRequirementMin', 'areaRequirementMax']);
              if (isNotEmpty(values.areaRequirementMin) && isNotEmpty(values.areaRequirementMax) && Number(values.areaRequirementMin) > Number(values.areaRequirementMax)) {
                V2Message.warning('面积范围填写有误，最小面积不能大于最大面积');
                return Promise.reject();
              }
              await postDirectTaskUpdate(buildLocxxDirectTaskUpdateParams({ id: taskDetail.id, ...values }));
              V2Message.success('编辑成功');
              refresh?.();
              return undefined;
            },
          };
        }
        if (key === 'activityPeriodName') {
          return {
            formCom: <div className={styles.activityPeriodEdit}>
              <V2FormInputNumber label='活动周期' name='activityPeriodCount' min={1} precision={0} />
              <V2FormSelect label='周期单位' name='activityPeriodUnit' options={[{ label: '日', value: 1 }, { label: '周', value: 2 }, { label: '月', value: 3 }]} />
            </div>,
            onClick() {
              form.setFieldsValue({ activityPeriodCount: taskDetail.activityPeriodCount, activityPeriodUnit: taskDetail.activityPeriodUnit });
            },
            onCancel() {
              form.setFieldsValue({ activityPeriodCount: taskDetail.activityPeriodCount, activityPeriodUnit: taskDetail.activityPeriodUnit });
            },
            position: 'rightBottom',
            onOK: async() => {
              const values = await form.validateFields(['activityPeriodCount', 'activityPeriodUnit']);
              await postDirectTaskUpdate(buildLocxxDirectTaskUpdateParams({ id: taskDetail.id, ...values }));
              V2Message.success('编辑成功');
              refresh?.();
            },
          };
        }
        if (key === 'floorHeight' || key === 'minFlowBottomLine' || key === 'totalBudget') {
          const config = key === 'floorHeight'
            ? { label: '层高要求', name: 'floorHeight', min: 0, precision: 1, addonAfter: 'm' }
            : key === 'minFlowBottomLine'
              ? { label: '客流底线', name: 'minFlowBottomLine', min: 0, precision: 0, addonAfter: '人/天' }
              : { label: '场地预算', name: 'totalBudget', min: 0, precision: 2, addonAfter: '万元' };
          return {
            formCom: <V2FormInputNumber label={config.label} name={config.name} min={config.min} precision={config.precision} config={{ addonAfter: config.addonAfter }} />,
            onClick() { form.setFieldValue(config.name, taskDetail[config.name]); },
            onCancel() { form.setFieldValue(config.name, taskDetail[config.name]); },
            onOK: async() => {
              const values = await form.validateFields([config.name]);
              await postDirectTaskUpdate(buildLocxxDirectTaskUpdateParams({ id: taskDetail.id, ...values }));
              V2Message.success('编辑成功');
              refresh?.();
            },
          };
        }
        if (key === 'promotionDate') {
          return {
            formCom: <V2FormRangePicker label='活动日期' name='promotionDate' formItemConfig={{ getValueFromEvent: (...[, dateString]) => dateString, getValueProps: getPromotionDateValueProps }} />,
            onClick() { form.setFieldValue('promotionDate', [taskDetail.promotionDateStart, taskDetail.promotionDateEnd]); },
            onCancel() { form.setFieldValue('promotionDate', [taskDetail.promotionDateStart, taskDetail.promotionDateEnd]); },
            onOK: async() => {
              const values = await form.validateFields(['promotionDate']);
              const dates = values.promotionDate || [];
              await postDirectTaskUpdate(buildLocxxDirectTaskUpdateParams({ id: taskDetail.id, promotionDateStart: dates[0], promotionDateEnd: dates[1] }));
              V2Message.success('编辑成功');
              refresh?.();
            },
          };
        }
        if (key === 'expectDropInDate') {
          return {
            formCom: <V2FormDatePicker label='期望落位日期' name='expectDropInDate' formItemConfig={{ getValueFromEvent: (...[, dateString]) => dateString, getValueProps: (value) => ({ value: value ? dayjs(value, 'YYYY-MM-DD') : undefined }) }} />,
            onClick() {
              form.setFieldValue('expectDropInDate', taskDetail.expectDropInDate);
            },
            onCancel() {
              form.setFieldValue('expectDropInDate', taskDetail.expectDropInDate);
            },
            onOK: async() => {
              const values = await form.validateFields(['expectDropInDate']);
              await postDirectTaskUpdate(buildLocxxDirectTaskUpdateParams({ id: taskDetail.id, ...values }));
              V2Message.success('编辑成功');
              refresh?.();
            },
          };
        }
      }
      if (key === 'manager') {
        return {
          formCom: <FormManager label='开发人员' placeholder='请选择开发人员' name='manager'/>,
          onCancel() {},
          onOK: async() => {
            const values = await form.validateFields();
            const val = values['manager'];
            if (!val) return;
            await clusterAndDirectTaskAssign({
              taskId: taskDetail?.id,
              assignAccountId: val
            });
            V2Message.success('指派成功');
            refresh?.();
          }
        };
      } else if (key === 'businessModeName') {
        return {
          formCom: <FormBusinessMode label='业务模式' placeholder='请选择业务模式' name='businessMode'/>,
          onCancel() {
            form.setFieldValue('businessMode', basicTaskInfo.businessMode);
          },
          onOK: async() => {
            await methods.updateTaskInfo('businessMode');
          }
        };
      } else if (key === 'emergencyDegreeName') {
        return {
          formCom: <FormEmergencyDegree label='紧急度' placeholder='请选择紧急度' name='emergencyDegree'/>,
          onCancel() {
            form.setFieldValue('emergencyDegree', basicTaskInfo.emergencyDegree);
          },
          onOK: async() => {
            await methods.updateTaskInfo('emergencyDegree');
          }
        };
      } else if (key === 'expectDropInDate') {
        return {
          formCom: <>
            {isStandardAndClusterBTaskTemplate
              ? <V2FormDatePicker label='期望商圈复核完成时间' name='expectDropInDate' required />
              : <V2FormDatePicker label='期望落位日期' name='expectDropInDate' />}
          </>,
          onCancel() {
            form.setFieldValue('expectDropInDate', dayjs(basicTaskInfo.expectDropInDate));
          },
          onOK: async() => {
            const val = form.getFieldValue('expectDropInDate');
            if (isStandardAndClusterBTaskTemplate && !isNotEmpty(val)) { // 此时必填
              form.validateFields(['expectDropInDate']);
              return Promise.reject();
            }
            return await methods.updateTaskInfo('expectDropInDate');
          }
        };
      } else if (key === 'standardFranchiseeName') {
        const frname = methods.franchiseeTitleForMode(basicTaskInfo.businessMode);
        const frShortName = franchiseeShortTitleForMode(basicTaskInfo.businessMode);
        return {
          formCom: <FormFranchisee
            label={frname}
            name='standardFranchiseeId'
            placeholder={`请输入${frname}`}
            createBtnTitle={`去创建${frShortName}`}
            allowClear
            extraParams={{
              businessMode: basicTaskInfo.businessMode,
            }}
          />,
          onCancel() {
            form.setFieldValue('standardFranchiseeId', basicTaskInfo.standardFranchiseeId);
          },
          onOK: async() => {
            // 解决onClick 之后没有选择直接保存的场景
            const targetValue = form.getFieldValue('standardFranchiseeId');
            if (isNotEmptyAny(targetValue) && !(+targetValue)) {
              form.setFieldValue('standardFranchiseeId', basicTaskInfo.standardFranchiseeId);
            }
            await methods.updateTaskInfo('standardFranchiseeId');
          },
          onClick() {
            // 解决点击编辑按钮时，输入框先显示id，然后<FormFranchisee/> 组件里loadData完之后才回显label的问题
            form.setFieldValue('standardFranchiseeId', basicTaskInfo.standardFranchiseeName);
          }
        };
      } else if (key === 'followingStatusName' && !followingStatusDisabled) { // 跟进状态
        return {
          formCom: <FormFollowingStatus
            disabled={followingStatusDisabled} // 如果是已确定、已开业不能修改
            label='跟进状态'
            placeholder='请选择跟进状态'
            name='followingStatus'
            isEdit/>,
          onCancel() {
            form.setFieldValue('followingStatus', basicTaskInfo.followingStatus);
          },
          onOK: async() => {
            await methods.updateTaskInfo('followingStatus');
          }
        };
      }
      return undefined;
    },

  });

  return <div className={cs(styles.basicWrapper, className || '')}>
    <div className={styles.basicInfo}>

      <div className={styles.basicMap}>
        <CircleTaskDetailMap
          businessDetail={taskDetail}
          chancePointList={chancePointList}
        />
      </div>

      <V2Title type='H2' divider text={title || `${expansionTaskAlias || '任务'}基本信息`} style={{ marginTop: '16px' }} />

      <V2Form form={form} validateTrigger={['onChange', 'onBlur']}>
        <V2DetailGroup>
          <Row gutter={16}>
            {
              Object.entries(isLocxx ? locxxDetailItemMap : TaskMap(methods.franchiseeTitleForMode(basicTaskInfo.businessMode), basicTaskInfo['standardFranchiseeUniqueName'])).map(([key, label], index) => {
                const detailItem = isLocxx ? label as { label: string; value: string } : undefined;
                const detailLabel = detailItem?.label || label as string;
                // 如果是加盟商 并且 业务模式不是加盟模式，则加盟商隐藏
                if (key === 'standardFranchiseeName' && ![BusinessMode.Franchisee, BusinessMode.Joint].includes(basicTaskInfo['businessMode'])) {
                  return <></>;
                }
                if (isStandardAndClusterBTaskTemplate && key === 'signShopCount') {
                  return <></>;
                }
                if (key === 'modelClusterName' && !isLocxx) {
                  label = openingAreaAlias as any;
                }
                if (isLocxx) {
                  return <Col span={8} key={index}><V2DetailItem
                    label={detailLabel}
                    value={key === 'modelClusterName' ? methods.getDetailItemValue(key) : detailItem?.value || '-'}
                    allowEdit={methods.getDetailItemAllowEdit(key)}
                    editConfig={methods.getDetailItemEditConfig(key)}
                  /></Col>;
                }
                if (key === 'expectDropInDate') {
                  label = isStandardAndClusterBTaskTemplate ? '期望商圈复核完成时间' : label;
                }
                return <Col span={8} key={index}>
                  <V2DetailItem
                    label={label as string}
                    allowEdit={methods.getDetailItemAllowEdit(key)}
                    value={methods.getDetailItemValue(key)}
                    editConfig={ methods.getDetailItemEditConfig(key) }/>
                </Col>;
              })
            }
          </Row>
        </V2DetailGroup>
      </V2Form>

    </div>
    <AreaDetailDrawer
      drawerData={drawerData}
      setDrawerData={setDrawerData}
      viewChanceDetail={false}
      showCollect={false}
    />
  </div>;

};

export default CircleBasicInfo;
